export type SpeakingEdge = 'start' | 'stop';

export interface SpeakingDetectorOptions {
    /** Continuous above-threshold time required before emitting 'start' (ms). */
    startMs: number;
    /** Continuous below-threshold time required before emitting 'stop' (ms). */
    stopMs: number;
}

export interface SpeakingDetector {
    /**
     * Feed one audio-level sample. Returns the edge crossed on this sample, or
     * null when the state is unchanged. `nowMs` must be a monotonic clock
     * reading (e.g. performance.now()); the detector measures elapsed wall-clock
     * time between samples rather than counting ticks, so an irregular or
     * throttled sample cadence doesn't skew the thresholds.
     */
    sample(isAboveThreshold: boolean, nowMs: number): SpeakingEdge | null;
    /** Whether the user is currently considered to be speaking. */
    isSpeaking(): boolean;
    /** Force back to the silent state without emitting an edge. */
    reset(): void;
}

/**
 * Hysteresis state machine for own-user speaking detection. It only flips state
 * once the contrary condition has held *continuously* for the configured
 * duration: you must be above the threshold for `startMs` before counting as
 * speaking, and below it for `stopMs` before counting as stopped. A single
 * contrary sample resets the in-progress run, so brief blips (a cough, a gap
 * between words) neither trip nor end an utterance.
 */
export const createSpeakingDetector = ({
    startMs,
    stopMs,
}: SpeakingDetectorOptions): SpeakingDetector => {
    let speaking = false;
    // When the run that could flip the current state began, or null when no
    // such run is in progress. In SILENT this tracks the above-threshold run; in
    // SPEAKING it tracks the below-threshold run.
    let candidateSince: number | null = null;

    return {
        sample(isAboveThreshold, nowMs) {
            // A sample matching the current state cancels any pending flip.
            if (isAboveThreshold === speaking) {
                candidateSince = null;
                return null;
            }

            if (candidateSince === null) {
                candidateSince = nowMs;
                return null;
            }

            const heldFor = nowMs - candidateSince;
            const requiredMs = speaking ? stopMs : startMs;
            if (heldFor >= requiredMs) {
                speaking = !speaking;
                candidateSince = null;
                return speaking ? 'start' : 'stop';
            }

            return null;
        },
        isSpeaking() {
            return speaking;
        },
        reset() {
            speaking = false;
            candidateSince = null;
        },
    };
};
